import { type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router';

// Not a real route — the Ask AI entry opens the chatbot panel instead of
// navigating, so this pathname only exists to key the nav item.
export const ASK_AI_PATHNAME = '/ask-ai';

// Same idea as ASK_AI_PATHNAME: the feedback entry opens a modal rather than
// navigating anywhere.
export const FEEDBACK_PATHNAME = '/give-feedback';

/** Whether a nav entry's pathname is a real route, and so can render a link. */
function isRoutePathname(pathname: string): boolean {
  return pathname !== ASK_AI_PATHNAME && pathname !== FEEDBACK_PATHNAME;
}

/**
 * Whether a click navigates this tab rather than opening a new one. Gates the
 * side effects that should only run when this tab is actually leaving the page.
 * Mirrors the `defaultPrevented` check react-router's `Link` applies after our
 * handler, so a cancelled click can't run the side effects without navigating.
 */
function isPlainLeftClick(event: MouseEvent<HTMLElement>): boolean {
  return (
    event.button === 0 &&
    !event.defaultPrevented &&
    !event.metaKey &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.shiftKey
  );
}

interface SidebarNavControlProps {
  children: ReactNode;
  className: string;
  isActive: boolean;
  /** Where the entry goes, or one of the pseudo-pathnames above. */
  pathname: string;
  /**
   * Side effects for activating the entry: resetting page state, closing the
   * mobile drawer, or opening the overlay for a pseudo-pathname. Must not
   * navigate — react-router's `Link` owns that for real routes.
   */
  onActivate: (path: string) => void;
  analyticsItem: string;
  analyticsSection?: string;
  ariaLabel?: string;
  testId?: string;
}

/**
 * Renders a nav entry as a react-router `<Link>` so it can be opened in a new
 * tab, copied, and announced as a link — falling back to a `<button>` for the
 * entries that open overlays instead (Ask AI, Give Feedback).
 *
 * `<Link>` rather than a hand-built `<a href>` so react-router applies the
 * `/:client` basename to the rendered href.
 */
export function SidebarNavControl({
  children,
  className,
  isActive,
  pathname,
  onActivate,
  analyticsItem,
  analyticsSection,
  ariaLabel,
  testId,
}: SidebarNavControlProps) {
  const sharedProps = {
    'aria-current': isActive ? ('page' as const) : undefined,
    'aria-label': ariaLabel,
    className,
    'data-amp-track-item': analyticsItem,
    'data-amp-track-section': analyticsSection,
    'data-testid': testId,
  };

  if (!isRoutePathname(pathname)) {
    return (
      <button
        type="button"
        {...sharedProps}
        onClick={() => onActivate(pathname)}
      >
        {children}
      </button>
    );
  }

  return (
    <Link
      {...sharedProps}
      to={pathname}
      onClick={(event) => {
        if (!isPlainLeftClick(event)) return;
        onActivate(pathname);
      }}
    >
      {children}
    </Link>
  );
}
